/**
 * `Waveform` — the played/unplayed meter. `docs/design-direction.md` §6.7, §9.5.
 *
 * **REAL PEAKS ONLY. THIS RULE HAS NO EXCEPTIONS AND NO "TEMPORARY" VERSION.**
 *
 * 512 `uint8` peaks are computed server-side from the actual audio and shipped
 * base64; this component draws them and nothing else. When `peaks` is `null` it
 * renders the *designed* no-peaks state — the one-drop bar ruler and a 3px `--accent`
 * progress bar in a `--border-strong` track, in the same 56px band — and there is no
 * code path here, not one, that could turn a hash, a duration or a title into a
 * plausible-looking signal. A fake waveform is the single thing that would expose the
 * whole premise as costume (§6.7, §12.6), and the anti-fake regression test asserts
 * that the null case emits no `<path>` at all.
 *
 * **One `<path>`, not 512 `<rect>`s.** `peaksToSvgPath` from `@bl/shared` walks the
 * top envelope left→right and the mirrored bottom envelope back, merging runs that
 * round to the same height. 512 elements would be 512 layout and paint boxes in
 * Telegram's Android webview, and they could not carry the one vertical flare→amber
 * gradient §6.7 asks for across the band. The played half is the **same geometry
 * painted twice**: a `<use>` of that path filled with `#playedInk` and clipped at the
 * playhead by `texture.css`'s `clip-path: inset(0 var(--rest) 0 0)`. A second `<path>`
 * with the same `d`, or a `<rect>` over the played region, would both duplicate the
 * geometry and break the one-path contract.
 *
 * **The playhead has no transition** (§7.2): a transition on a playhead always lags
 * the audio. Three custom properties carry the position and are written together on
 * one frame — `--rest` (the clip inset), `--played` (the head and loupe position) and
 * `--wave-progress` (the no-peaks bar's `scaleX`). React renders them from `progress`
 * so the first paint is correct; a 60fps owner writes them straight onto the node it
 * gets from `ref`, with no render per frame.
 *
 * The whole 56px band is the scrub target — the 1px head never is (§5.6). `Scrub`
 * wraps this component and owns the pointer, the keyboard and the loupe.
 */

import { useMemo } from 'react';
import type { CSSProperties, JSX, Ref } from 'react';

import { peaksToSvgPath } from '@bl/shared';

import styles from './components.module.css';
import { Ruler } from './primitives/Ruler';
import { cx } from './primitives/Rule';

/** The path's user-space box. Stretched to the band by `preserveAspectRatio="none"`. */
const VIEW_W = 512;
const VIEW_H = 56;

/**
 * `texture.css` hard-codes `fill: url('#playedInk')`, so the gradient's id is part of
 * the recipe rather than a generated one. There is exactly one waveform on screen at
 * a time — the Now Playing band (§8.1) — which is what makes a fixed id safe.
 */
const GRADIENT_ID = 'playedInk';
const PATH_ID = 'blWavePath';

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  return value >= 1 ? 1 : value;
}

/** The three position properties, written together so they can never disagree. */
export function waveProgressVars(progress: number): CSSProperties {
  const played = clamp01(progress);
  return {
    '--rest': `${(1 - played) * 100}%`,
    '--played': `${played * 100}%`,
    '--wave-progress': String(played),
  } as CSSProperties;
}

export interface WaveformProps {
  /** Exactly 512 server-computed peaks, or `null`. Never synthesised (§6.7). */
  readonly peaks: Uint8Array | null;
  /** `0..1`. Clamped, never `NaN` — an unknown duration reads as 0, not as a guess. */
  readonly progress: number;
  /** Forwarded to the band so a 60fps owner can write the position properties. */
  readonly ref?: Ref<HTMLDivElement> | undefined;
  readonly className?: string | undefined;
}

export function Waveform({ peaks, progress, ref, className }: WaveformProps): JSX.Element {
  const style = waveProgressVars(progress);

  const path = useMemo(
    () => (peaks === null || peaks.length === 0 ? null : peaksToSvgPath(peaks, VIEW_W, VIEW_H)),
    [peaks],
  );

  if (path === null) {
    /*
     * The designed no-peaks state (§6.7, §9.5): the bar ruler, a 3px `--accent`
     * progress bar in a `--border-strong` track, and NOTHING ELSE. No path, no head,
     * no placeholder shape — the band simply says "no waveform for this cut" while
     * remaining the same 56px scrub target.
     */
    return (
      <div
        ref={ref}
        className={cx('wave', styles.wave, styles.bare, className)}
        style={style}
        data-peaks="none"
      >
        <Ruler variant="scale" />
        <span className={styles.track} aria-hidden="true">
          <span className={styles.fill} />
        </span>
      </div>
    );
  }

  return (
    <div ref={ref} className={cx('wave', styles.wave, className)} style={style} data-peaks="real">
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          {/* Bass energy prints in hot ink at the axis; peaks reach amber (§6.7). */}
          <linearGradient id={GRADIENT_ID} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" className={styles.stopAxis} />
            <stop offset="1" className={styles.stopPeak} />
          </linearGradient>
        </defs>
        <path id={PATH_ID} className="wave__unplayed" d={path} />
        {/* The same geometry, re-inked and clipped at the playhead. */}
        <use href={`#${PATH_ID}`} className="wave__played" />
      </svg>
      <span className={cx('wave__head', styles.head)} aria-hidden="true" />
    </div>
  );
}
